/**
 * ══════════════════════════════════════════════════════════════
 * NETUNO — Engine de Diagnóstico (Root Cause Analysis)
 * ══════════════════════════════════════════════════════════════
 *
 * Analisa o contexto dos dados e gera diagnósticos de causa raiz
 * para insights detectados pelo InsightEngine.
 *
 * Uso: import { generateDiagnostic } from '@/lib/diagnosticEngine';
 * ══════════════════════════════════════════════════════════════
 */

import type { AnalyticalInsight } from '@/types/insights';
import type {
  RootCauseAnalysis,
  ProbableCause,
  AffectedEntity,
  DiagnosticAction,
  DiagnosticContext,
} from '@/types/diagnostics';

// ─── Diagnostic Generators (by rule code) ───────────────────

type DiagnosticGenerator = (
  insight: AnalyticalInsight,
  ctx: DiagnosticContext,
) => RootCauseAnalysis | null;

const GENERATORS: Record<string, DiagnosticGenerator> = {
  // ── R1: Concentração de risco na carteira
  RISK_CONCENTRATION: (insight, ctx) => {
    if (!ctx.portfolio || !ctx.contracts) return null;

    const criticos = ctx.contracts.filter(c => c.nivel_criticidade === 'critico');
    const clusters = new Map<string, number>();
    criticos.forEach(c => {
      const k = c.cluster_nome || 'Sem cluster';
      clusters.set(k, (clusters.get(k) || 0) + 1);
    });
    const topCluster = [...clusters.entries()].sort((a, b) => b[1] - a[1])[0];

    const causes: ProbableCause[] = [];

    if (topCluster && topCluster[1] >= 2) {
      causes.push({
        id: 'conc-cluster',
        label: `Concentração no cluster "${topCluster[0]}"`,
        description: `${topCluster[1]} dos ${criticos.length} contratos críticos pertencem ao cluster "${topCluster[0]}", sugerindo fator regional ou operacional comum.`,
        confidence: 75,
        category: 'concentration_risk',
        evidence: [
          { type: 'metric', label: 'Contratos críticos no cluster', value: `${topCluster[1]}/${criticos.length}` },
          { type: 'pattern', label: 'Concentração geográfica', value: topCluster[0] },
        ],
      });
    }

    const highDelay = criticos.filter(c => (c.prob_atraso_mc ?? 0) > 60);
    if (highDelay.length >= 2) {
      causes.push({
        id: 'conc-delay',
        label: 'Padrão de atraso sistêmico',
        description: `${highDelay.length} contratos críticos apresentam probabilidade de atraso >60%, indicando problema estrutural de prazo.`,
        confidence: 80,
        category: 'delay_pattern',
        evidence: [
          { type: 'metric', label: 'Prob. atraso média', value: `${Math.round(highDelay.reduce((s, c) => s + (c.prob_atraso_mc ?? 0), 0) / highDelay.length)}%` },
          { type: 'threshold', label: 'Limiar', value: '>60%' },
        ],
      });
    }

    const highOccurrences = criticos.filter(c => (c.total_ocorrencias ?? 0) >= 5);
    if (highOccurrences.length >= 2) {
      causes.push({
        id: 'conc-quality',
        label: 'Problemas de qualidade recorrentes',
        description: `${highOccurrences.length} contratos críticos possuem 5+ ocorrências, indicando deficiência operacional.`,
        confidence: 65,
        category: 'quality_issues',
        evidence: [
          { type: 'metric', label: 'Contratos com ocorrências elevadas', value: `${highOccurrences.length}` },
          { type: 'pattern', label: 'Tipo', value: 'Não-conformidades recorrentes' },
        ],
      });
    }

    if (causes.length === 0) {
      causes.push({
        id: 'conc-generic',
        label: 'Múltiplos fatores de risco',
        description: 'A concentração de risco é resultado de múltiplos indicadores elevados sem uma causa dominante clara.',
        confidence: 50,
        category: 'concentration_risk',
        evidence: [
          { type: 'metric', label: 'Contratos críticos', value: `${criticos.length}/${ctx.portfolio.total}` },
        ],
      });
    }

    const affected: AffectedEntity[] = criticos.slice(0, 5).map(c => ({
      type: 'contract' as const,
      id: c.contract_id,
      name: c.contrato_nome,
      impact: c.score_criticidade >= 90 ? 'high' as const : 'medium' as const,
      drillDownRoute: `/empreendimentos/inteligencia/${c.contract_id}`,
    }));

    const actions: DiagnosticAction[] = [
      {
        id: 'act-review-critical',
        title: 'Revisão executiva dos contratos críticos',
        description: 'Convocar reunião de análise para os contratos com score ≥80 e definir planos de mitigação.',
        priority: 'immediate',
        drillDownRoute: '/empreendimentos/risk-analytics',
      },
      {
        id: 'act-investigate-cluster',
        title: 'Investigar causas regionais',
        description: 'Analisar se há fatores comuns (fornecedor, região, tipo de obra) entre os contratos críticos.',
        priority: 'short_term',
        drillDownRoute: '/empreendimentos/inteligencia/uo',
      },
    ];

    return buildDiagnostic(insight, causes, affected, actions);
  },

  // ── R2: Deterioração generalizada
  PORTFOLIO_DETERIORATION: (insight, ctx) => {
    if (!ctx.portfolio || !ctx.contracts) return null;

    const piorando = ctx.contracts.filter(c => c.tendencia === 'piorando');
    const causes: ProbableCause[] = [];

    const pctCriticoPiorando = piorando.filter(c => c.nivel_criticidade === 'critico').length;
    if (pctCriticoPiorando >= 2) {
      causes.push({
        id: 'det-critical-worsening',
        label: 'Contratos críticos em degradação contínua',
        description: `${pctCriticoPiorando} contratos já em nível crítico continuam se deteriorando, indicando ineficácia das medidas atuais.`,
        confidence: 85,
        category: 'trend_systemic',
        evidence: [
          { type: 'metric', label: 'Críticos piorando', value: `${pctCriticoPiorando}` },
          { type: 'pattern', label: 'Tendência', value: 'Degradação contínua' },
        ],
      });
    }

    const highDelayPiorando = piorando.filter(c => (c.prob_atraso_mc ?? 0) > 50);
    if (highDelayPiorando.length >= 2) {
      causes.push({
        id: 'det-delay-systemic',
        label: 'Atraso sistêmico em ascensão',
        description: `${highDelayPiorando.length} contratos em deterioração têm prob. atraso >50%, sugerindo causa estrutural.`,
        confidence: 70,
        category: 'delay_pattern',
        evidence: [
          { type: 'metric', label: 'Contratos com atraso crescente', value: `${highDelayPiorando.length}` },
          { type: 'correlation', label: 'Correlação', value: 'Tendência + prob. atraso elevada' },
        ],
      });
    }

    if (causes.length === 0) {
      causes.push({
        id: 'det-generic',
        label: 'Deterioração generalizada sem causa dominante',
        description: 'Múltiplos contratos estão piorando simultaneamente, possivelmente por fatores macroeconômicos ou sazonais.',
        confidence: 45,
        category: 'external_factor',
        evidence: [
          { type: 'metric', label: 'Contratos piorando', value: `${piorando.length}/${ctx.portfolio.total}` },
        ],
      });
    }

    const affected: AffectedEntity[] = piorando.slice(0, 5).map(c => ({
      type: 'contract' as const,
      id: c.contract_id,
      name: c.contrato_nome,
      impact: c.nivel_criticidade === 'critico' ? 'high' as const : 'medium' as const,
      drillDownRoute: `/empreendimentos/inteligencia/${c.contract_id}`,
    }));

    const actions: DiagnosticAction[] = [
      {
        id: 'act-trend-analysis',
        title: 'Análise de tendências detalhada',
        description: 'Investigar o dashboard de tendências para identificar padrões comuns entre contratos em deterioração.',
        priority: 'immediate',
        drillDownRoute: '/empreendimentos/inteligencia/tendencias',
      },
      {
        id: 'act-mitigation-plans',
        title: 'Revisar planos de mitigação existentes',
        description: 'Avaliar se os planos de ação atuais estão sendo eficazes ou precisam de reajuste.',
        priority: 'short_term',
      },
    ];

    return buildDiagnostic(insight, causes, affected, actions);
  },

  // ── R3: Contrato com score extremo
  HIGH_SCORE_CONTRACT: (insight, ctx) => {
    if (!ctx.contracts || !insight.relatedEntity) return null;

    const contract = ctx.contracts.find(c => c.contract_id === insight.relatedEntity!.id);
    if (!contract) return null;

    const causes: ProbableCause[] = [];

    if ((contract.prob_atraso_mc ?? 0) > 70) {
      causes.push({
        id: `hsc-delay-${contract.contract_id}`,
        label: 'Probabilidade de atraso muito elevada',
        description: `Probabilidade Monte Carlo de atraso em ${contract.prob_atraso_mc}%, principal contribuinte para o score extremo.`,
        confidence: 90,
        category: 'delay_pattern',
        evidence: [
          { type: 'metric', label: 'Prob. atraso MC', value: `${contract.prob_atraso_mc}%`, metric: 'prob_atraso_mc' },
          { type: 'threshold', label: 'Limiar crítico', value: '>70%' },
        ],
      });
    }

    if ((contract.total_ocorrencias ?? 0) >= 5) {
      causes.push({
        id: `hsc-occurrences-${contract.contract_id}`,
        label: 'Alto volume de não-conformidades',
        description: `${contract.total_ocorrencias} ocorrências registradas indicam problemas operacionais persistentes.`,
        confidence: 70,
        category: 'quality_issues',
        evidence: [
          { type: 'metric', label: 'Total ocorrências', value: `${contract.total_ocorrencias}`, metric: 'total_ocorrencias' },
        ],
      });
    }

    if (contract.tendencia === 'piorando') {
      causes.push({
        id: `hsc-trend-${contract.contract_id}`,
        label: 'Tendência de deterioração ativa',
        description: 'O contrato está em trajetória de piora, aumentando a probabilidade de falha.',
        confidence: 75,
        category: 'trend_systemic',
        evidence: [
          { type: 'pattern', label: 'Tendência', value: 'Piorando' },
        ],
      });
    }

    if (causes.length === 0) {
      causes.push({
        id: `hsc-multi-${contract.contract_id}`,
        label: 'Múltiplos indicadores de risco elevados',
        description: 'O score extremo resulta da combinação de diversos fatores de risco acima dos limiares.',
        confidence: 55,
        category: 'concentration_risk',
        evidence: [
          { type: 'metric', label: 'Score criticidade', value: `${contract.score_criticidade}` },
        ],
      });
    }

    const affected: AffectedEntity[] = [{
      type: 'contract',
      id: contract.contract_id,
      name: contract.contrato_nome,
      impact: 'high',
      drillDownRoute: `/empreendimentos/inteligencia/${contract.contract_id}`,
    }];

    const actions: DiagnosticAction[] = [
      {
        id: `act-review-${contract.contract_id}`,
        title: 'Revisão executiva imediata',
        description: `Avaliar contrato "${contract.contrato_nome}" com equipe de gestão e definir contingências.`,
        priority: 'immediate',
        drillDownRoute: `/empreendimentos/inteligencia/${contract.contract_id}`,
      },
      {
        id: `act-contingency-${contract.contract_id}`,
        title: 'Elaborar plano de contingência',
        description: 'Definir ações preventivas caso os indicadores continuem se deteriorando.',
        priority: 'short_term',
      },
    ];

    return buildDiagnostic(insight, causes, affected, actions);
  },

  // ── R4: Alto volume de ocorrências
  HIGH_OCCURRENCES: (insight, ctx) => {
    if (!ctx.contracts || !insight.relatedEntity) return null;

    const contract = ctx.contracts.find(c => c.contract_id === insight.relatedEntity!.id);
    if (!contract) return null;

    const causes: ProbableCause[] = [{
      id: `occ-quality-${contract.contract_id}`,
      label: 'Deficiência de qualidade operacional',
      description: `${contract.total_ocorrencias} ocorrências indicam problemas recorrentes de execução, qualidade ou segurança.`,
      confidence: 80,
      category: 'quality_issues',
      evidence: [
        { type: 'metric', label: 'Total ocorrências', value: `${contract.total_ocorrencias}` },
        { type: 'threshold', label: 'Limiar', value: '≥10' },
      ],
    }];

    if (contract.tendencia === 'piorando') {
      causes.push({
        id: `occ-trend-${contract.contract_id}`,
        label: 'Tendência de piora associada',
        description: 'Além das ocorrências, o contrato está em trajetória de deterioração.',
        confidence: 65,
        category: 'trend_systemic',
        evidence: [
          { type: 'correlation', label: 'Correlação', value: 'Ocorrências ↑ + Tendência ↓' },
        ],
      });
    }

    const affected: AffectedEntity[] = [{
      type: 'contract',
      id: contract.contract_id,
      name: contract.contrato_nome,
      impact: 'high',
      drillDownRoute: `/empreendimentos/inteligencia/${contract.contract_id}`,
    }];

    const actions: DiagnosticAction[] = [
      {
        id: `act-quality-${contract.contract_id}`,
        title: 'Acionar equipe de qualidade',
        description: 'Verificar padrão de recorrência das não-conformidades e revisar processos.',
        priority: 'immediate',
      },
    ];

    return buildDiagnostic(insight, causes, affected, actions);
  },

  // ── R5: UO com concentração de risco
  UO_RISK_CONCENTRATION: (insight, ctx) => {
    if (!ctx.uos) return null;

    const criticalUOs = ctx.uos.filter(u => u.nivel_risco === 'critico');
    const causes: ProbableCause[] = [];

    const highContractUOs = criticalUOs.filter(u => u.total_contratos >= 3);
    if (highContractUOs.length > 0) {
      causes.push({
        id: 'uo-high-volume',
        label: 'UOs com alto volume de contratos críticos',
        description: `${highContractUOs.length} UO(s) com 3+ contratos contribuem para a concentração de risco regional.`,
        confidence: 70,
        category: 'concentration_risk',
        evidence: [
          { type: 'metric', label: 'UOs com 3+ contratos', value: `${highContractUOs.length}` },
        ],
      });
    }

    const highScoreUOs = criticalUOs.filter(u => u.score_medio >= 75);
    if (highScoreUOs.length > 0) {
      causes.push({
        id: 'uo-high-score',
        label: 'Score médio elevado em UOs críticas',
        description: `Score médio ≥75 em ${highScoreUOs.length} UO(s), indicando que os contratos individuais já são de alto risco.`,
        confidence: 75,
        category: 'concentration_risk',
        evidence: highScoreUOs.slice(0, 3).map(u => ({
          type: 'metric' as const,
          label: u.uo_nome,
          value: `Score médio: ${u.score_medio.toFixed(0)}`,
        })),
      });
    }

    if (causes.length === 0) {
      causes.push({
        id: 'uo-generic',
        label: 'Risco distribuído entre múltiplas UOs',
        description: 'Não foi identificada uma causa dominante; o risco é resultado de fatores distribuídos.',
        confidence: 40,
        category: 'concentration_risk',
        evidence: [
          { type: 'metric', label: 'UOs críticas', value: `${criticalUOs.length}` },
        ],
      });
    }

    const affected: AffectedEntity[] = criticalUOs.slice(0, 5).map(u => ({
      type: 'uo' as const,
      id: u.uo_id,
      name: u.uo_nome,
      impact: u.score_medio >= 80 ? 'high' as const : 'medium' as const,
    }));

    const actions: DiagnosticAction[] = [
      {
        id: 'act-uo-prioritize',
        title: 'Priorizar UOs de maior impacto',
        description: 'Concentrar ações nas UOs com maior número de contratos críticos e score médio mais elevado.',
        priority: 'immediate',
        drillDownRoute: '/empreendimentos/inteligencia/uo',
      },
    ];

    return buildDiagnostic(insight, causes, affected, actions);
  },

  // ── R7: Risco crescente
  GROWING_RISK_TREND: (insight, ctx) => {
    if (!ctx.contracts || !insight.relatedEntity) return null;

    const contract = ctx.contracts.find(c => c.contract_id === insight.relatedEntity!.id);
    if (!contract) return null;

    const causes: ProbableCause[] = [{
      id: `grt-trend-${contract.contract_id}`,
      label: 'Aceleração na probabilidade de atraso',
      description: 'A probabilidade de atraso aumentou significativamente entre períodos consecutivos, indicando deterioração ativa.',
      confidence: 80,
      category: 'delay_pattern',
      evidence: [
        { type: 'pattern', label: 'Direção', value: 'Piorando' },
        { type: 'metric', label: 'Score atual', value: `${contract.score_criticidade}` },
      ],
    }];

    const affected: AffectedEntity[] = [{
      type: 'contract',
      id: contract.contract_id,
      name: contract.contrato_nome,
      impact: contract.score_criticidade >= 80 ? 'high' : 'medium',
      drillDownRoute: `/empreendimentos/inteligencia/${contract.contract_id}`,
    }];

    const actions: DiagnosticAction[] = [
      {
        id: `act-investigate-${contract.contract_id}`,
        title: 'Investigar causa raiz do aumento',
        description: 'Analisar cronograma, medições e inspeções recentes para identificar o fator de deterioração.',
        priority: 'immediate',
        drillDownRoute: `/empreendimentos/inteligencia/${contract.contract_id}`,
      },
    ];

    return buildDiagnostic(insight, causes, affected, actions);
  },
};

// ─── Builder Helper ─────────────────────────────────────────

function buildDiagnostic(
  insight: AnalyticalInsight,
  causes: ProbableCause[],
  affected: AffectedEntity[],
  actions: DiagnosticAction[],
): RootCauseAnalysis {
  const sortedCauses = [...causes].sort((a, b) => b.confidence - a.confidence);
  const avgConfidence = Math.round(
    sortedCauses.reduce((s, c) => s + c.confidence, 0) / sortedCauses.length
  );

  return {
    id: `diag-${insight.id}`,
    relatedInsightId: insight.id,
    summary: buildSummary(insight, sortedCauses),
    severity: insight.severity,
    probableCauses: sortedCauses,
    confidenceScore: avgConfidence,
    affectedEntities: affected,
    recommendedActions: actions,
    generatedAt: new Date().toISOString(),
  };
}

function buildSummary(insight: AnalyticalInsight, causes: ProbableCause[]): string {
  if (causes.length === 0) return insight.description;
  const topCause = causes[0];
  return `${insight.title}. Causa principal identificada (${topCause.confidence}% confiança): ${topCause.label}.`;
}

// ─── Public API ─────────────────────────────────────────────

/**
 * Generate a root cause diagnostic for a given insight.
 * Returns null if no diagnostic generator exists for the insight's rule.
 */
export function generateDiagnostic(
  insight: AnalyticalInsight,
  context: DiagnosticContext,
): RootCauseAnalysis | null {
  const ruleCode = insight.ruleCode;
  if (!ruleCode) return null;

  const generator = GENERATORS[ruleCode];
  if (!generator) return null;

  try {
    return generator(insight, context);
  } catch (err) {
    console.warn(`[DiagnosticEngine] Failed for rule ${ruleCode}:`, err);
    return null;
  }
}

/**
 * Generate diagnostics for multiple insights at once.
 */
export function generateDiagnostics(
  insights: AnalyticalInsight[],
  context: DiagnosticContext,
): Map<string, RootCauseAnalysis> {
  const results = new Map<string, RootCauseAnalysis>();

  for (const insight of insights) {
    const diagnostic = generateDiagnostic(insight, context);
    if (diagnostic) {
      results.set(insight.id, diagnostic);
    }
  }

  return results;
}
